import { escape, includes, isEmpty } from 'lodash';
import React from 'react';

// Updates the entire array with the same value when a single object is updated.
export const fillArray: <Type>(length: number, arrayElement: Type) => Type[] = (
  length,
  arrayElement,
) => {
  return Array.from({ length })
    .fill(undefined)
    .map(() => {
      return { ...arrayElement };
    });
};

/**
 * Sanitizes input text for safe HTML rendering.
 */
export const sanitizeInput = <T extends Record<string, string>>(
  payload: T,
): T => {
  return Object.fromEntries(
    Object.entries(payload).map(([key, value]) => [key, escape(value)]),
  ) as T;
};

/**
 * Clamp and sanitize a decimal number input string.
 *
 * - Removes extra minus signs (keeps only a leading one if allowed)
 * - Ensures only a single decimal point is present
 * - Limits the number of fractional digits to maxDecimals
 * - Returns null for empty or incomplete inputs like '-', '.', '-.'
 *
 * @param raw Input string from a text/number field
 * @param maxDecimals Maximum number of digits allowed after the decimal point
 * @param allowNegative Whether to allow a leading negative sign
 * @returns Parsed number or null if not a valid value yet
 */
export const clampDecimalInput = (
  raw: string,
  maxDecimals: number,
  allowNegative = false,
): number | null => {
  if (isEmpty(raw)) {
    return null;
  }

  let sanitizedInput = String(raw);

  if (allowNegative) {
    sanitizedInput = sanitizedInput.replace(/(?!^)-/g, '');
  } else {
    sanitizedInput = sanitizedInput.replace(/-/g, '');
  }

  const decimalPointIndex = sanitizedInput.indexOf('.');

  if (decimalPointIndex !== -1) {
    const integerPartWithDot = sanitizedInput.slice(0, decimalPointIndex + 1);
    let fractionalPart = sanitizedInput
      .slice(decimalPointIndex + 1)
      .replace(/\./g, '');

    if (fractionalPart.length > maxDecimals) {
      fractionalPart = fractionalPart.slice(0, maxDecimals);
    }

    sanitizedInput = integerPartWithDot + fractionalPart;
  }

  if (includes(['-', '.', '-.'], sanitizedInput)) {
    return null;
  }

  const parsedNumber = Number(sanitizedInput);

  return Number.isNaN(parsedNumber) ? null : parsedNumber;
};

export const preventNonNumber = (
  event: React.KeyboardEvent<HTMLInputElement>,
) => {
  // These are characters that are allowed in number input.
  if (event.key === 'e' || event.key === '-' || event.key === '+') {
    event.preventDefault();
  }
};

/**
 * To prevent non-integer input in an input field by blocking
 * non-numeric characters and the decimal point.
 */
export const preventNonInteger = (
  event: React.KeyboardEvent<HTMLInputElement>,
) => {
  preventNonNumber(event);
  if (event.key === '.') {
    event.preventDefault();
  }
};

/**
 * Keydown handler that allows negative numbers but blocks 'e' and '+' characters.
 * Useful for numeric inputs where negatives are valid and scientific notation is not.
 *
 * @param event React keyboard event from an input element
 */
export const allowNegativeNumberKeyDown = (
  event: React.KeyboardEvent<HTMLInputElement>,
) => {
  if (event.key === 'e' || event.key === '+') {
    event.preventDefault();
  }
};

/**
 * The binarySearchNearestValue function finds the index of the element in the array that is nearest to the given number.
 * @param {number[]} array - An array of number values.
 * @param {number} value - A number for which the nearest value in the array is to be found.
 * @returns `index` - the index of nearest number to the given value
 */
export const binarySearchNearestValue = (
  array: number[],
  value: number,
): number => {
  let start = 0;
  let end = array.length - 1;

  while (start <= end) {
    const midIndex = Math.floor((start + end) / 2);
    const midValue = array[midIndex];

    if (midValue === value) {
      return midIndex;
    } else if (midValue < value) {
      start = midIndex + 1;
    } else {
      end = midIndex - 1;
    }
  }

  // 'end' might go below 0 and 'start' might exceed the array length, so we need to clamp their values.
  // 'lowerIndex' is the largest index that is less than or equal to the target value.
  const lowerIndex = Math.max(end, 0);

  // 'higherIndex' is the smallest index that is greater than or equal to the target value.
  const higherIndex = Math.min(start, array.length - 1);

  const lowerDiff = value - array[lowerIndex];
  const higherDiff = array[higherIndex] - value;
  const index = lowerDiff <= higherDiff ? lowerIndex : higherIndex;

  return index;
};
